export interface AIAnalysisResult {
  website?: {
    url: string
    colors: Array<{
      hex: string
      name: string
      confidence: number
      usage: string
    }>
    fonts: Array<{
      family: string
      weight: string
      confidence: number
      usage: string
    }>
    brandElements: Array<{
      type: 'logo' | 'tagline' | 'value' | 'tone'
      content: string
      confidence: number
      location: string
    }>
    tone: {
      primary: string
      attributes: string[]
      confidence: number
    }
    industry: {
      detected: string
      confidence: number
      competitors: string[]
    }
  }
  socialMedia?: {
    platforms: Array<{
      name: string
      handle: string
      followers: number
      engagement: number
      brandConsistency: number
    }>
    contentThemes: string[]
    postingFrequency: string
    audienceInsights: {
      demographics: string
      interests: string[]
      engagement_patterns: string
    }
  }
  recommendations: Array<{
    type: 'color' | 'typography' | 'messaging' | 'strategy'
    title: string
    description: string
    confidence: number
    actionable: boolean
  }>
  confidence: number
  processingTime: number
}

export interface AIAssetRequest {
  type: 'logo' | 'business_card' | 'social_post' | 'presentation' | 'email_header'
  brandContext: {
    name: string
    industry: string
    colors: string[]
    fonts: string[]
    tone: string
    values: string[]
  }
  specifications: {
    dimensions?: string
    format?: string
    style?: string
    content?: string
  }
}

export interface AIAssetResult {
  id: string
  type: string
  url: string
  thumbnail: string
  variations: Array<{
    id: string
    url: string
    description: string
  }>
  metadata: {
    dimensions: string
    format: string
    style: string
    confidence: number
  }
  downloadUrls: {
    png?: string
    svg?: string
    pdf?: string
  }
}

class MockAIService {
  async analyzeWebsite(url: string): Promise<AIAnalysisResult> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 2000 + Math.random() * 3000))
    
    const mockResults: AIAnalysisResult = {
      website: {
        url,
        colors: [
          { hex: '#2563EB', name: 'Primary Blue', confidence: 0.92, usage: 'Primary brand color, CTAs, headers' },
          { hex: '#1E40AF', name: 'Deep Blue', confidence: 0.85, usage: 'Secondary elements, hover states' },
          { hex: '#F8FAFC', name: 'Light Gray', confidence: 0.78, usage: 'Backgrounds, subtle sections' },
          { hex: '#334155', name: 'Dark Gray', confidence: 0.88, usage: 'Body text, descriptions' },
          { hex: '#10B981', name: 'Success Green', confidence: 0.72, usage: 'Success states, positive actions' }
        ],
        fonts: [
          { family: 'Inter', weight: '400,500,600', confidence: 0.94, usage: 'Primary font for headings and body' },
          { family: 'Roboto', weight: '400,500', confidence: 0.76, usage: 'Secondary font for supporting text' }
        ],
        brandElements: [
          { type: 'tagline', content: 'Innovation Through Technology', confidence: 0.89, location: 'Hero section, about page' },
          { type: 'value', content: 'Quality, Innovation, Trust', confidence: 0.82, location: 'About section' },
          { type: 'tone', content: 'Professional yet approachable', confidence: 0.77, location: 'Content analysis' }
        ],
        tone: {
          primary: 'Professional',
          attributes: ['Trustworthy', 'Innovative', 'Approachable', 'Expert'],
          confidence: 0.84
        },
        industry: {
          detected: 'Technology',
          confidence: 0.91,
          competitors: ['TechFlow', 'InnovateLab', 'DigitalCorp']
        }
      },
      socialMedia: {
        platforms: [
          { name: 'LinkedIn', handle: '@company', followers: 2500, engagement: 4.2, brandConsistency: 87 },
          { name: 'Twitter', handle: '@company', followers: 1200, engagement: 3.8, brandConsistency: 92 }
        ],
        contentThemes: ['Product Updates', 'Industry Insights', 'Company Culture', 'Client Success'],
        postingFrequency: '3-4 posts per week',
        audienceInsights: {
          demographics: 'Professionals aged 25-45, primarily in tech industry',
          interests: ['Technology', 'Innovation', 'Business Growth', 'Digital Solutions'],
          engagement_patterns: 'High engagement on educational content and case studies'
        }
      },
      recommendations: [
        {
          type: 'color',
          title: 'Enhance Color Accessibility',
          description: 'Consider adding a lighter blue variant for better accessibility compliance',
          confidence: 0.85,
          actionable: true
        },
        {
          type: 'typography',
          title: 'Consistent Font Weights',
          description: 'Standardize font weights across headers to improve visual hierarchy',
          confidence: 0.78,
          actionable: true
        },
        {
          type: 'messaging',
          title: 'Strengthen Value Proposition',
          description: 'Your innovation message is strong - consider emphasizing speed and reliability',
          confidence: 0.82,
          actionable: true
        },
        {
          type: 'strategy',
          title: 'Social Media Consistency',
          description: 'Great brand consistency on Twitter - apply similar approach to LinkedIn',
          confidence: 0.88,
          actionable: true
        }
      ],
      confidence: 0.86,
      processingTime: 4200
    }

    return mockResults
  }

  async generateAsset(request: AIAssetRequest): Promise<AIAssetResult> {
    // Simulate generation delay
    await new Promise(resolve => setTimeout(resolve, 3000 + Math.random() * 5000))

    const assetId = `ai_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    
    const mockAsset: AIAssetResult = {
      id: assetId,
      type: request.type,
      url: `https://picsum.photos/800/600?random=${assetId}`,
      thumbnail: `https://picsum.photos/200/150?random=${assetId}`,
      variations: [
        {
          id: `${assetId}_v1`,
          url: `https://picsum.photos/800/600?random=${assetId}_1`,
          description: 'Modern minimalist style'
        },
        {
          id: `${assetId}_v2`,
          url: `https://picsum.photos/800/600?random=${assetId}_2`,
          description: 'Bold and vibrant approach'
        },
        {
          id: `${assetId}_v3`,
          url: `https://picsum.photos/800/600?random=${assetId}_3`,
          description: 'Classic professional look'
        }
      ],
      metadata: {
        dimensions: request.specifications.dimensions || '800x600',
        format: request.specifications.format || 'PNG',
        style: request.specifications.style || 'Modern',
        confidence: 0.85 + Math.random() * 0.15
      },
      downloadUrls: {
        png: `https://example.com/download/${assetId}.png`,
        svg: `https://example.com/download/${assetId}.svg`,
        pdf: `https://example.com/download/${assetId}.pdf`
      }
    }

    return mockAsset
  }

  async getSuggestions(context: {
    type: 'colors' | 'fonts' | 'messaging' | 'positioning' | 'strategy'
    brandData?: any
    currentData?: any
  }): Promise<Array<{
    type: string
    title: string
    description: string
    value?: any
    confidence: number
  }>> {
    // Simulate AI thinking time
    await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 2000))

    const suggestions = {
      colors: [
        {
          type: 'color',
          title: 'Accessible Blue Palette',
          description: 'Based on your industry, these blues convey trust and professionalism',
          value: ['#2563EB', '#1E40AF', '#3B82F6', '#60A5FA'],
          confidence: 0.92
        },
        {
          type: 'color',
          title: 'Complementary Accent',
          description: 'Add warmth with this orange that complements your primary blue',
          value: ['#F97316', '#EA580C'],
          confidence: 0.78
        },
        {
          type: 'color',
          title: 'Success & Error States',
          description: 'Professional colors for system feedback and status indicators',
          value: ['#10B981', '#EF4444', '#F59E0B'],
          confidence: 0.85
        }
      ],
      fonts: [
        {
          type: 'font',
          title: 'Inter for Modern Tech',
          description: 'Perfect for technology brands - clean, readable, and contemporary',
          value: { family: 'Inter', weights: ['400', '500', '600'] },
          confidence: 0.94
        },
        {
          type: 'font',
          title: 'Roboto for Body Text',
          description: 'Excellent readability for longer content and documentation',
          value: { family: 'Roboto', weights: ['400', '500'] },
          confidence: 0.87
        }
      ],
      messaging: [
        {
          type: 'tagline',
          title: 'Innovation-Focused',
          description: 'Emphasizes your forward-thinking approach',
          value: 'Innovating Tomorrow\'s Solutions Today',
          confidence: 0.88
        },
        {
          type: 'value_prop',
          title: 'Problem-Solution Focus',
          description: 'Addresses customer pain points directly',
          value: 'Transforming Complex Challenges into Simple Solutions',
          confidence: 0.82
        }
      ],
      positioning: [
        {
          type: 'market_position',
          title: 'Innovation Leader',
          description: 'Position as the innovative choice in your market',
          value: 'The technology partner that turns your vision into reality',
          confidence: 0.85
        },
        {
          type: 'differentiator',
          title: 'Speed & Quality',
          description: 'Your competitive advantage in delivery and excellence',
          value: 'Faster delivery without compromising quality',
          confidence: 0.79
        }
      ],
      strategy: [
        {
          type: 'target_audience',
          title: 'Tech-Savvy Professionals',
          description: 'Based on market analysis, focus on decision-makers in tech',
          value: 'CTOs, Product Managers, and Tech Entrepreneurs',
          confidence: 0.91
        },
        {
          type: 'channel_strategy',
          title: 'Content-Driven Growth',
          description: 'Your audience responds well to educational content',
          value: 'Blog, webinars, and thought leadership content',
          confidence: 0.83
        }
      ]
    }

    return suggestions[context.type] || []
  }

  async getInsights(brandId: string): Promise<Array<{
    type: 'performance' | 'opportunity' | 'warning' | 'success'
    title: string
    description: string
    metric?: number
    recommendation?: string
    priority: 'high' | 'medium' | 'low'
  }>> {
    await new Promise(resolve => setTimeout(resolve, 1500))
    
    return [
      {
        type: 'success',
        title: 'Strong Brand Consistency',
        description: 'Your color usage across different touchpoints is highly consistent',
        metric: 92,
        recommendation: 'Keep maintaining this consistency as you expand',
        priority: 'low'
      },
      {
        type: 'opportunity',
        title: 'Typography Hierarchy',
        description: 'Opportunity to strengthen visual hierarchy with better font weight distribution',
        metric: 68,
        recommendation: 'Define clearer heading styles and spacing rules',
        priority: 'medium'
      },
      {
        type: 'warning',
        title: 'Accessibility Concerns',
        description: 'Some color combinations may not meet WCAG AA standards',
        metric: 45,
        recommendation: 'Review contrast ratios and add accessible alternatives',
        priority: 'high'
      },
      {
        type: 'performance',
        title: 'Social Media Engagement',
        description: 'Your brand content is performing well on professional platforms',
        metric: 78,
        recommendation: 'Consider expanding to more visual platforms',
        priority: 'medium'
      }
    ]
  }
}

export const mockAI = new MockAIService()

// Helper functions for AI-powered features
export const simulateAIProcessing = (minTime = 1000, maxTime = 3000) => {
  return new Promise(resolve => {
    setTimeout(resolve, minTime + Math.random() * (maxTime - minTime))
  })
}

export const generateAIConfidence = () => {
  return 0.7 + Math.random() * 0.3 // Between 70-100%
}

export const formatConfidence = (confidence: number) => {
  return `${Math.round(confidence * 100)}% confident`
}